import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { PRODUCTS_QUERY, storefrontApiRequest, formatMoney, productImageAlt, shopifyImg, shopifySrcSet, type ShopifyProduct } from "@/lib/shopify";
import hero from "@/assets/hero.jpg";
// @ts-expect-error vite-imagetools transforms query at build time
import heroAvif from "@/assets/hero.jpg?format=avif&w=1600&quality=70";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWebp from "@/assets/hero.jpg?format=webp&w=1600&quality=75";

const igluQueryOptions = {
  queryKey: ["products", "iglu-landing"] as const,
  queryFn: async () => {
    // Fetch all products and filter by IGLU vendor on the client — mirrors the
    // logic used on /kauppa?kategoria=iglu so both pages show the same set.
    const res = await storefrontApiRequest(PRODUCTS_QUERY, { first: 100, query: null });
    const all = (res?.data?.products?.edges ?? []) as ShopifyProduct[];
    return all.filter((p) => /iglu/i.test((p.node as unknown as { vendor?: string }).vendor ?? ""));
  },
};

export const Route = createFileRoute("/iglu")({
  loader: ({ context }) => {
    // Await so SSR renders the product grid — matches /kauppa behaviour and
    // prevents the empty-state flash on first paint.
    return context.queryClient.ensureQueryData(igluQueryOptions);
  },
  head: () => ({
    meta: [
      { title: "IGLU Blocks — pehmopalikat lapselle 6 kk–6 v | Montessori24" },
      { name: "description", content: "IGLU-pehmopalikat kiipeilyyn, ryömimiseen ja majanrakennukseen — ohjattua liikkumista sisätiloissa. Tuotekohtaiset turvallisuustiedot, ilmainen toimitus 3–7 pv, 30 pv palautusetu." },
      { property: "og:title", content: "IGLU Blocks — pehmopalikat lapselle 6 kk–6 v" },
      { property: "og:description", content: "Turvalliset pehmopalikat kiipeilyyn ja majanrakennukseen. Ilmainen toimitus Suomeen 3–7 pv." },
      { property: "og:url", content: "https://montessori24.fi/iglu" },
      { property: "og:image", content: "https://montessori24.fi/og-image.jpg" },
      { name: "twitter:image", content: "https://montessori24.fi/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://montessori24.fi/iglu" },
      { rel: "preload", as: "image", href: heroWebp, type: "image/webp", fetchPriority: "high" },
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi/" },
          { "@type": "ListItem", position: 2, name: "Kauppa", item: "https://montessori24.fi/kauppa" },
          { "@type": "ListItem", position: 3, name: "IGLU Blocks", item: "https://montessori24.fi/iglu" },
        ],
      }),
    }, {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Mille ikäryhmälle IGLU Blocks sopii?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Palikat taipuvat sekä pienimmän lapsen turvalliseksi alustaksi että isomman rakenteluleikkiin. Tarkista tuotekohtainen ikäsuositus tuotesivulta.",
            },
          },
          {
            "@type": "Question",
            name: "Onko materiaali turvallinen?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Valmistajan ilmoittamat materiaali- ja turvallisuustiedot löydät tuotesivulta. Pinnat ovat pehmeät ja saumat kestävät leikkiä.",
            },
          },
          {
            "@type": "Question",
            name: "Miten kauan toimitus kestää?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "3–7 arkipäivässä koko Suomeen. Toimitus on aina ilmainen.",
            },
          },
          {
            "@type": "Question",
            name: "Voinko palauttaa, jos tuote ei sovi?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Kyllä, 30 päivän palautusetu ilman perusteluja (lakisääteinen 14 pv peruuttamisoikeus).",
            },
          },
        ],
      }),
    }],
  }),
  component: IgluLanding,
});

function IgluLanding() {
  const { data: products } = useQuery(igluQueryOptions);

  return (
    <Layout>
      {/* Trust ribbon */}
      <div className="border-b border-border bg-cream">
        <div className="container-prose flex flex-wrap items-center justify-center gap-x-6 gap-y-1 py-2 text-center text-xs text-muted-foreground">
          <span>✓ Ilmainen toimitus</span>
          <span className="hidden sm:inline">·</span>
          <span>✓ Toimitus 3–7 päivää</span>
          <span className="hidden sm:inline">·</span>
          <span>✓ 30 päivän palautusetu</span>
        </div>
      </div>

      {/* Hero — single CTA, product-first */}
      <section className="relative">
        <div className="container-prose grid items-center gap-8 pt-8 pb-12 md:grid-cols-12 md:gap-10 md:pt-20 md:pb-20">
          <div className="order-2 md:order-1 md:col-span-5">
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Uutuus · IGLU Blocks</span>
            <h1 className="mt-4 text-4xl leading-[1.05] text-balance text-foreground md:mt-6 md:text-7xl md:leading-[1.02]">
              Pehmeä leikki,<br/>iso liike.
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground md:mt-6">
              IGLU Blocks on modulaarinen pehmopalikkasetti, joka tuo turvallisen
              kiipeilyn ja tasapainoilun olohuoneeseen. Kasvaa lapsen mukana
              6 kk → 6 v.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#mallisto"
                className="inline-flex items-center justify-center rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Katso mallisto →
              </a>
            </div>
          </div>
          <div className="order-1 md:order-2 md:col-span-7">
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-muted">
              <picture>
                <source type="image/avif" srcSet={heroAvif} />
                <source type="image/webp" srcSet={heroWebp} />
                <img
                  src={hero}
                  alt="IGLU Blocks pehmopalikat lastenhuoneessa"
                  width={1600}
                  height={1200}
                  className="h-full w-full object-cover"
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* Mallisto */}
      <section id="mallisto" className="container-prose border-t border-border pt-12 pb-16 md:pt-20 md:pb-24">
        <div className="flex items-end justify-between">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Mallisto</span>
            <h2 className="mt-3 text-4xl text-foreground md:text-5xl">IGLU Blocks</h2>
          </div>
        </div>

        {products && products.length > 0 ? (
          <div className="mt-8 grid gap-x-4 gap-y-10 grid-cols-2 lg:grid-cols-4 md:mt-12 md:gap-x-6 md:gap-y-14">
            {products.map((p) => {
              const img = p.node.images.edges[0]?.node;
              const price = p.node.priceRange.minVariantPrice;
              return (
                <Link key={p.node.id} to="/product/$handle" params={{ handle: p.node.handle }} className="group block">
                  <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-muted">
                    {img && (
                      <img
                        src={shopifyImg(img.url, 400)}
                        srcSet={shopifySrcSet(img.url, 400)}
                        sizes="(min-width: 1024px) 280px, 50vw"
                        alt={productImageAlt(img.altText, p.node.title)}
                        width={400}
                        height={500}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    )}
                  </div>
                  <div className="mt-3 md:mt-4">
                    <h3 className="text-sm text-foreground md:text-base">{p.node.title}</h3>
                    <div className="mt-1 text-sm text-foreground">{formatMoney(price.amount, price.currencyCode)}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="mt-12 rounded-xl border border-dashed border-border bg-cream py-16 text-center">
            <p className="text-foreground">Mallisto julkaistaan pian.</p>
          </div>
        )}
      </section>

      {/* Mini-FAQ */}
      <section className="container-prose border-t border-border py-16 md:py-24">
        <h2 className="text-3xl text-foreground md:text-4xl">Yleisimmät kysymykset</h2>
        <dl className="mt-8 divide-y divide-border border-y border-border max-w-3xl">
          {[
            ["Mille ikäryhmälle IGLU Blocks sopii?", "Palikat taipuvat sekä pienimmän lapsen turvalliseksi alustaksi että isomman rakenteluleikkiin. Tarkista tuotekohtainen ikäsuositus tuotesivulta."],
            ["Onko materiaali turvallinen?", "Valmistajan ilmoittamat materiaali- ja turvallisuustiedot löydät tuotesivulta. Pinnat ovat pehmeät ja saumat kestävät leikkiä."],
            ["Miten kauan toimitus kestää?", "3–7 arkipäivässä koko Suomeen. Toimitus on aina ilmainen."],
            ["Voinko palauttaa, jos tuote ei sovi?", "Kyllä, 30 päivän palautusetu ilman perusteluja (lakisääteinen 14 pv peruuttamisoikeus)."],
          ].map(([q, a]) => (
            <details key={q} className="group py-5">
              <summary className="flex cursor-pointer list-none items-start justify-between gap-6 text-base text-foreground">
                <span>{q}</span>
                <span className="mt-1 text-xl text-muted-foreground transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 max-w-prose text-sm leading-relaxed text-muted-foreground">{a}</p>
            </details>
          ))}
        </dl>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border bg-foreground text-background">
        <div className="container-prose py-20 text-center md:py-28">
          <h2 className="text-4xl md:text-5xl text-balance">Aloita pehmeä leikki tänään.</h2>
          <p className="mt-4 mx-auto max-w-xl text-background/75">
            Ilmainen toimitus koko Suomeen ja 30 päivän palautusetu.
          </p>
          <a
            href="#mallisto"
            className="mt-8 inline-flex items-center justify-center rounded-full bg-background px-8 py-4 text-sm font-medium text-foreground transition-transform hover:-translate-y-0.5"
          >
            Katso mallisto →
          </a>
        </div>
      </section>
    </Layout>
  );
}